import { useState } from 'react';
import { useIntl } from 'react-intl';
import useAuth from 'src/hooks/useAuth';
import HoverCard from 'src/componentes/hover-card';
import { capitalizeFirstLetter } from 'src/utils/string';
import {
  getColorByScore,
  getGrauByScore,
} from '../utils/matriz_com_score.utils';
import TabelaMatrizRiscoLayout, {
  BadgeMatrizRisco,
  CelulaMatrizRisco,
  CodigosMatrizRisco,
} from './TabelaMatrizRiscoLayout';
import {
  aplicarFiltroQuadrante,
  obterAvaliacoesQuadrante,
  obterCodigosItens,
  quadranteContemSelecionados,
} from './tabela.utils';
import { criarRenderCelulaGrau, VISAO_MATRIZ } from './VisaoGrauMatrizRisco';

const TabelaMatrizRiscoComScore = ({
  matrizFarol,
  riscoAvaliacoesOriginal,
  idsSelecionados,
  setFilter,
  setPositionMouse,
  positionMouse,
  positionInerente,
  inerente,
  header,
  apenasMaiorQueZero,
  exibirCodigoItem,
  informarScore,
  isWidget,
  heightBase,
  visao = VISAO_MATRIZ.QUANTIDADE,
  setFilterGrau,
}) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const [grauEmFoco, setGrauEmFoco] = useState(null);
  if (!matrizFarol) return null;

  const {
    scoreSeveridades = [],
    scoreProbabilidades = [],
    grauMatrizRisco = [],
    riscoAvaliacoes = [],
    inverter,
  } = matrizFarol;
  const obterCor = getColorByScore(grauMatrizRisco || []);
  const obterGrau = getGrauByScore(grauMatrizRisco || []);
  const linhas = (
    (inverter ? scoreSeveridades : scoreProbabilidades) || []
  ).map((item) => ({
    ...(inverter ? item.severidade : item.probabilidade),
    scoreItem: item,
  }));
  const colunas = (
    (inverter ? scoreProbabilidades : scoreSeveridades) || []
  ).map((item) => ({
    ...(inverter ? item.probabilidade : item.severidade),
    scoreItem: item,
  }));

  const renderGrupo = (avaliacoes, tipoInerente) => {
    if (!avaliacoes.length) return null;
    const codigos = obterCodigosItens(avaliacoes);
    const score = tipoInerente
      ? avaliacoes[0].scoreInerente
      : avaliacoes[0].score;
    const Conteudo = exibirCodigoItem ? CodigosMatrizRisco : BadgeMatrizRisco;

    return (
      <HoverCard
        placement="right"
        className="tw:max-w-sm"
        title={capitalizeFirstLetter(
          intl.formatMessage(
            {
              id: tipoInerente ? 'labelRiscoInerente' : 'labelRiscoResidual',
            },
            { risco: resources.risco }
          )
        )}
        content={
          <div className="tw:flex tw:flex-col tw:gap-2">
            {score != null && (
              <p className="tw:mb-0 tw:font-bold">
                {intl.formatMessage({
                  id: tipoInerente
                    ? 'labelScoreInerente'
                    : 'labelScoreResidual',
                })}
                : {score}
              </p>
            )}
            <p className="tw:mb-0">
              {avaliacoes
                .map((avaliacao) => avaliacao.codigo)
                .filter(Boolean)
                .join(', ')}
            </p>
          </div>
        }
      >
        <span tabIndex={0} className="tw:max-w-full">
          <Conteudo title={exibirCodigoItem ? codigos : undefined}>
            {intl.formatMessage({
              id: tipoInerente
                ? 'labelSiglaRiscoInerente'
                : 'labelSiglaRiscoResidual',
            })}{' '}
            ({exibirCodigoItem ? codigos : avaliacoes.length})
          </Conteudo>
        </span>
      </HoverCard>
    );
  };

  const renderCelulaGrau =
    visao === VISAO_MATRIZ.GRAU_RISCO &&
    criarRenderCelulaGrau({
      linhas,
      colunas,
      obterQuadrante: (linha, coluna) => {
        const severidade = (inverter ? linha : coluna).scoreItem;
        const probabilidade = (inverter ? coluna : linha).scoreItem;
        const score = severidade.score * probabilidade.score;
        return {
          severidade,
          probabilidade,
          cor: obterCor(score),
          chaveGrau: obterGrau(score)?.id,
        };
      },
      riscoAvaliacoes,
      riscoAvaliacoesOriginal,
      idsSelecionados,
      inerente,
      setFilterGrau,
      setPositionMouse,
      grauEmFoco,
      setGrauEmFoco,
    });

  return (
    <TabelaMatrizRiscoLayout
      header={header}
      linhas={linhas}
      colunas={colunas}
      labelLinhas={inverter ? resources.severidade : resources.probabilidade}
      labelColunas={inverter ? resources.probabilidade : resources.severidade}
      isWidget={isWidget}
      heightBase={heightBase}
      positionMouse={positionMouse}
      positionInerente={positionInerente}
      inerente={inerente}
      setPositionMouse={setPositionMouse}
      renderCelula={(linha, coluna, indexColuna) => {
        if (renderCelulaGrau)
          return renderCelulaGrau(linha, coluna, indexColuna);
        const severidade = (inverter ? linha : coluna).scoreItem;
        const probabilidade = (inverter ? coluna : linha).scoreItem;
        const inerentes = obterAvaliacoesQuadrante(
          riscoAvaliacoes,
          severidade,
          probabilidade,
          true
        );
        const residuais = obterAvaliacoesQuadrante(
          riscoAvaliacoes,
          severidade,
          probabilidade,
          false
        );
        const avaliacoes = inerente ? inerentes : residuais;
        const destacada = quadranteContemSelecionados(
          avaliacoes,
          idsSelecionados
        );
        const hasScore = inerentes.length > 0 || residuais.length > 0;
        const codigos = obterCodigosItens(avaliacoes);
        const key = linha.id + '-' + coluna.id;

        return (
          <CelulaMatrizRisco
            key={key}
            cor={obterCor(severidade.score * probabilidade.score)}
            selecionada={idsSelecionados?.length > 0 && destacada}
            desbotada={
              !destacada ||
              (!idsSelecionados?.length && informarScore && !hasScore)
            }
            label={linha.nome + ' / ' + coluna.nome}
            onClick={() =>
              aplicarFiltroQuadrante({
                avaliacoes: riscoAvaliacoes,
                riscoAvaliacoesOriginal,
                idsSelecionados,
                severidade,
                probabilidade,
                inerente,
                setFilter,
              })
            }
            onMouseEnter={() => setPositionMouse?.(indexColuna, inerente)}
            onMouseLeave={() => setPositionMouse?.(-1)}
          >
            {informarScore ? (
              <>
                {renderGrupo(inerentes, true)}
                {renderGrupo(residuais, false)}
              </>
            ) : (
              (!apenasMaiorQueZero || avaliacoes.length > 0) &&
              (exibirCodigoItem ? (
                <CodigosMatrizRisco title={codigos}>
                  {codigos}
                </CodigosMatrizRisco>
              ) : (
                <BadgeMatrizRisco>{avaliacoes.length}</BadgeMatrizRisco>
              ))
            )}
          </CelulaMatrizRisco>
        );
      }}
    />
  );
};

export default TabelaMatrizRiscoComScore;
